import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { OrganizationDetailFacade } from '../../../../../Facades/Organizations/organization-detail.facade';
import { OrganizationEditPayload } from '../../../Interfaces/organization-edit.interface';
import { OrganizationDetail } from '../../../../../Models/backoffice/organization-detail.model';

@Component({
  selector: 'av-organization-edit',
  standalone: true,
  imports: [ReactiveFormsModule, MatIconModule, TranslateModule],
  templateUrl: './organization-edit.component.html',
  styleUrl: './organization-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class OrganizationEditComponent {
  private readonly _formBuilder = inject(FormBuilder);

  readonly facade = inject(OrganizationDetailFacade);


  readonly formGroup: FormGroup = this._formBuilder.group({
    name: ['', [Validators.maxLength(150)]],
    domain: ['', [Validators.maxLength(255)]],
    avyna_nickname: ['', [Validators.maxLength(255)]],
    contact_email: ['', [Validators.email, Validators.maxLength(255)]],
    billing_currency: ['', [Validators.minLength(3), Validators.maxLength(3)]],
    market_id: [''],
  });

  constructor() {
    this._registerOrganizationSync();
  }

  submit(): void {
    if (!this.formGroup.valid) {
      return;
    }

    this.facade.save(this._payload());
  }

  reset(): void {
    this._applyOrganization(this.facade.organization());
  }

  private _registerOrganizationSync(): void {
    effect(() => {
      const organization = this.facade.organization();

      this._applyOrganization(organization);
    });
  }

  private _applyOrganization(organization: OrganizationDetail | null): void {
    if (!organization) {
      return;
    }

    this.formGroup.reset({
      name: organization.name ?? '',
      domain: organization.domain ?? '',
      avyna_nickname: organization.avyna_nickname ?? '',
      contact_email: organization.contact_email ?? '',
      billing_currency: organization.billing_currency ?? '',
      market_id: organization.market?.id ?? '',
    });
  }

  private _payload(): OrganizationEditPayload {
    const value = this.formGroup.value;

    return {
      name: this._nullable(value.name),
      domain: this._nullable(value.domain),
      avyna_nickname: this._nullable(value.avyna_nickname),
      contact_email: this._nullable(value.contact_email),
      billing_currency: this._nullable(value.billing_currency),
      market_id: this._nullable(value.market_id),
    };
  }

  private _nullable(value: unknown): string | null {
    const text = String(value ?? '').trim();

    return text === '' ? null : text;
  }
}
